import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    RightOutlined,
    SearchOutlined,
    UserOutlined,
    CarOutlined,
    DollarOutlined,
    SettingOutlined,
    HistoryOutlined,
    PhoneOutlined,
    IdcardOutlined,
    CalendarOutlined,
    TrophyOutlined,
    ClockCircleOutlined,
    ArrowLeftOutlined,
    SwapRightOutlined,
    HomeOutlined,
    HeartOutlined,
    TeamOutlined,
    AlertOutlined,
    SmileOutlined
} from '@ant-design/icons';
import { partenairesService, settingsService } from '../../common/services';
import {
    Spin, Descriptions, Table, Tag, Typography, Card, Breadcrumb, Tabs,
    DatePicker, Select, Input, Row, Col, Grid, Progress, Avatar, Statistic, Tooltip, Button
} from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Fonction pour générer une couleur à partir du nom
const getAvatarColor = (name) => {
    const colors = [
        '#1890ff', '#52c41a', '#faad14', '#eb2f96', '#722ed1',
        '#13c2c2', '#fa541c', '#2f54eb', '#a0d911', '#f5222d'
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

const DetailPartenaire = () => {
    const { id } = useParams();

    const [partenaire, setPartenaire] = useState(null);
    const [loading, setLoading] = useState(true);

    // Opérations (serveur)
    const [opsItems, setOpsItems] = useState([]);
    const [opsTotal, setOpsTotal] = useState(0);
    const [opsPage, setOpsPage] = useState(1);
    const [opsLimit, setOpsLimit] = useState(10);
    const [opsSort, setOpsSort] = useState('-date');
    const [opsLoading, setOpsLoading] = useState(false);

    // Filtres opérations
    const [opsTypes, setOpsTypes] = useState([]);
    const [opsDateRange, setOpsDateRange] = useState(null);
    const [opsQuery, setOpsQuery] = useState('');

    // Historique de points (client)
    const [pointHistory, setPointHistory] = useState([]);
    const [phDateRange, setPhDateRange] = useState(null);
    const [phType, setPhType] = useState(undefined);
    const [phQuery, setPhQuery] = useState('');
    const [opsSum, setOpsSum] = useState(0);
    const [phPage, setPhPage] = useState(1);
    const [phLimit, setPhLimit] = useState(5);

    // Breakpoints & tailles adaptatives
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;
    const controlSize = isMobile ? 'small' : 'middle';

    // Settings dynamiques (valeurs par défaut en fallback)
    const [settings, setSettings] = useState({
        BONUS_55_THRESHOLD: 55,
        BONUS_HEURES_SEUIL: 130
    });

    const GOAL_55 = settings.BONUS_55_THRESHOLD;
    const GOAL_130H = settings.BONUS_HEURES_SEUIL;

    const bonus55Start = partenaire?.bonus55?.countingFrom ? dayjs(partenaire.bonus55.countingFrom) : null;
    const bonus55AwardedAt = partenaire?.bonus55?.awardedAt ? dayjs(partenaire.bonus55.awardedAt) : null;
    const bonus55Awarded = !!bonus55AwardedAt;
    const coursesSinceStart = Number(partenaire?.statistiquesCourses?.total ?? 0);
    const progress55 = Math.min(100, Math.round((coursesSinceStart / GOAL_55) * 100));
    const bonus55NotStarted = bonus55Start && bonus55Start.isAfter(dayjs());

    // Variables pour le bonus 130 heures
    const heuresCeMois = Number(partenaire?.statistiquesHeuresMensuelles?.heures ?? 0);
    const bonus130hAwarded = !!partenaire?.bonusCentTrenteHeuresAttribue;
    const progress130h = Math.min(100, Math.round((heuresCeMois / GOAL_130H) * 100));
    const moisEnCours = partenaire?.statistiquesHeuresMensuelles?.mois || dayjs().format('YYYY-MM');

    // Filtrage pointHistory
    const filteredPointHistory = useMemo(() => {
        return (pointHistory || []).filter((e) => {
            const d = dayjs(e.date);
            const okDate = !phDateRange || (
                d.valueOf() >= phDateRange[0].startOf('day').valueOf() &&
                d.valueOf() <= phDateRange[1].endOf('day').valueOf()
            );
            const okType = !phType || e.type === phType;
            const q = (phQuery || '').trim().toLowerCase();
            const okQ = !q || (e.description?.toLowerCase().includes(q) || e.phone?.toLowerCase().includes(q));
            return okDate && okType && okQ;
        });
    }, [pointHistory, phDateRange, phType, phQuery]);

    const phTotalPoints = useMemo(() => {
        return (filteredPointHistory || []).reduce((acc, e) => acc + (Number(e.points) || 0), 0);
    }, [filteredPointHistory]);

    // Chargement initial
    useEffect(() => {
        if (!id) return;
        setLoading(true);

        // Charger les paramètres système en parallèle
        const loadSettings = async () => {
            try {
                const response = await settingsService.getAll();
                // Gérer les deux formats possibles: tableau direct ou { data: [...] }
                const allSettings = Array.isArray(response) ? response : (response?.data || response?.items || []);
                const settingsMap = {};
                allSettings.forEach(s => {
                    if (s && s.key) {
                        settingsMap[s.key] = s.value;
                    }
                });
                setSettings(prev => ({
                    ...prev,
                    BONUS_55_THRESHOLD: Number(settingsMap.BONUS_55_THRESHOLD) || 55,
                    BONUS_HEURES_SEUIL: Number(settingsMap.BONUS_HEURES_SEUIL) || 130
                }));
            } catch (err) {
                console.error('Erreur chargement settings :', err);
            }
        };

        loadSettings();

        partenairesService.getById(id)
            .then((data) => {
                setPartenaire(data);
                const ops = data?.operations || {};
                setOpsItems(ops.items || []);
                setOpsTotal(ops.total || 0);
                setPointHistory(ops.pointHistory || []);
            })
            .catch((err) => console.error('Erreur chargement partenaire :', err))
            .finally(() => setLoading(false));
    }, [id]);

    // Fetch opérations avec filtres
    const fetchOperations = async () => {
        if (!id) return;
        try {
            setOpsLoading(true);
            const params = {
                page: opsPage,
                limit: opsLimit,
                sort: opsSort,
                q: opsQuery || undefined,
                types: opsTypes.length ? opsTypes : undefined,
                start: opsDateRange?.[0]?.format('YYYY-MM-DD'),
                end: opsDateRange?.[1]?.format('YYYY-MM-DD'),
            };
            const data = await partenairesService.getByIdWithQuery(id, params);
            setPartenaire((prev) => ({ ...(prev || {}), ...data }));
            const ops = data?.operations || {};
            setOpsItems(ops.items || []);
            setOpsTotal(ops.total || 0);
            setOpsSum(ops.sumAmount || 0);
            if (ops.pointHistory) setPointHistory(ops.pointHistory);
        } catch (e) {
            console.error('Erreur fetchOperations:', e);
        } finally {
            setOpsLoading(false);
        }
    };

    useEffect(() => {
        if (!partenaire) return;
        fetchOperations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opsPage, opsLimit, opsSort, opsTypes, opsDateRange, opsQuery]);

    useEffect(() => {
        setPhPage(1);
    }, [phDateRange, phType, phQuery]);

    const formatPhoneNumber = (phone) => {
        if (!phone) return '-';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `+225 ${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`;
        }
        return phone.startsWith('+') ? phone : `+225 ${phone}`;
    };

    const formatDate = (date) => {
        if (!date) return '-';
        const parsed = dayjs(date);
        return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : '-';
    };

    const format2 = (v) => (v === null || v === undefined || v === '' || isNaN(Number(v)))
        ? '—'
        : Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Colonnes véhicules
    const columnsVehicules = [
        {
            title: 'Véhicule',
            key: 'vehicule',
            render: (_, r) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar size={36} style={{ backgroundColor: '#1890ff' }} icon={<CarOutlined />} />
                    <div>
                        <div style={{ fontWeight: 500 }}>{r.marque} {r.modele}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.annee} - {r.couleur}</div>
                    </div>
                </div>
            )
        },
        {
            title: 'Immatriculation',
            dataIndex: 'immatriculation',
            render: (immat) => <Tag color="blue" style={{ fontWeight: 500 }}>{immat}</Tag>
        },
        {
            title: 'Catégorie',
            dataIndex: ['categorieServiceId', 'name'],
            render: (name) => name ? <Tag color="purple">{name}</Tag> : '-',
        },
        {
            title: 'Classes service',
            dataIndex: 'categories',
            render: (types) => (types || []).map((cat, idx) => <Tag key={idx} color="green">{cat}</Tag>),
        },
    ];

    // Types opérations
    const TYPE_LABEL = {
        sos_credit: 'SOS Crédit',
        transfert: 'Transfert',
        vehicule_modifie: 'Changement véhicule',
        promo_dimanche: 'Promo dimanche',
        changement_numero: 'Changement n°',
    };
    const TYPE_COLOR = {
        sos_credit: 'purple',
        transfert: 'geekblue',
        vehicule_modifie: 'volcano',
        promo_dimanche: 'gold',
        changement_numero: 'cyan',
    };

    const columnsOperations = [
        {
            title: 'Type',
            dataIndex: 'type',
            width: 150,
            render: (t) => <Tag color={TYPE_COLOR[t] || 'default'}>{TYPE_LABEL[t] || t}</Tag>,
        },
        {
            title: 'Détails',
            render: (_, r) => {
                if (r.type === 'transfert') {
                    return (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Tag color="blue">{formatPhoneNumber(r.senderPhone)}</Tag>
                            <SwapRightOutlined style={{ color: '#8c8c8c' }} />
                            <Tag color="green">{formatPhoneNumber(r.receiverPhone)}</Tag>
                        </span>
                    );
                }
                if (r.type === 'vehicule_modifie') {
                    return (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Tag color="red">{r.meta?.ancienneImmatriculation}</Tag>
                            <SwapRightOutlined style={{ color: '#8c8c8c' }} />
                            <Tag color="green">{r.meta?.nouvelleImmatriculation}</Tag>
                        </span>
                    );
                }
                if (r.type === 'changement_numero') {
                    return (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Tag color="red">{formatPhoneNumber(r.meta?.ancienNumero)}</Tag>
                            <SwapRightOutlined style={{ color: '#8c8c8c' }} />
                            <Tag color="green">{formatPhoneNumber(r.meta?.nouveauNumero)}</Tag>
                        </span>
                    );
                }
                if (r.type === 'promo_dimanche') {
                    return <Tag color="gold">Activation règle dimanche</Tag>;
                }
                return <Tag>{formatPhoneNumber(r.phone)}</Tag>;
            },
        },
        {
            title: 'Montant',
            dataIndex: 'amount',
            width: 120,
            align: 'right',
            render: (a) => a ? (
                <Text strong style={{ color: '#52c41a' }}>{Number(a).toLocaleString('fr-FR')} FCFA</Text>
            ) : '—',
        },
        {
            title: 'Date',
            dataIndex: 'date',
            width: 150,
            sorter: true,
            render: (d) => (
                <span style={{ color: '#595959' }}>
                    <CalendarOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
                    {formatDate(d)}
                </span>
            ),
        },
    ];

    const onOpsTableChange = (pagination, _filters, sorter) => {
        setOpsPage(pagination.current || 1);
        setOpsLimit(pagination.pageSize || 10);
        if (sorter?.field === 'date') {
            setOpsSort(sorter.order === 'ascend' ? 'date' : '-date');
        }
    };

    // Style des lignes alternées
    const rowClassName = (_, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark';

    if (loading || !partenaire) {
        return (
            <div style={{ padding: 40, textAlign: 'center', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin tip="Chargement des détails..." size="large" />
            </div>
        );
    }

    const fullName = `${partenaire.firstName || ''} ${partenaire.lastName || ''}`.trim();
    const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    const points = Number(partenaire?.soldePoints?.point ?? 0);

    // Onglets config
    const tabItems = [
        {
            key: '1',
            label: (
                <span><UserOutlined style={{ marginRight: 6 }} />Driver</span>
            ),
            children: (
                <Card
                    style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    bodyStyle={{ padding: isMobile ? 12 : 24 }}
                >
                    <Descriptions
                        bordered
                        column={{ xs: 1, sm: 1, md: 2 }}
                        size={controlSize}
                        labelStyle={{
                            fontWeight: 500,
                            backgroundColor: '#fafafa',
                            width: isMobile ? 140 : 180
                        }}
                        contentStyle={{ backgroundColor: 'white' }}
                    >
                        <Descriptions.Item label={<><UserOutlined style={{ marginRight: 8, color: '#fa8c16' }} />Nom complet</>}>
                            <Text strong>{fullName}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={<><PhoneOutlined style={{ marginRight: 8, color: '#52c41a' }} />Téléphone</>}>
                            <Tag color="blue" style={{ fontSize: 13 }}>{formatPhoneNumber(partenaire.phone)}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label={<><IdcardOutlined style={{ marginRight: 8, color: '#722ed1' }} />Permis de conduire</>}>
                            <Text code>{partenaire.numeroPermisConduire || '-'}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={<><IdcardOutlined style={{ marginRight: 8, color: '#1890ff' }} />Identifiant</>}>
                            <Text code style={{ fontSize: 11 }}>{partenaire.id}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 8, color: '#13c2c2' }} />Créé le</>}>
                            {formatDate(partenaire.createdAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label={<><IdcardOutlined style={{ marginRight: 8, color: '#eb2f96' }} />Driver Profil ID</>}>
                            <Text code style={{ fontSize: 11 }}>{partenaire.driverProfilId || '-'}</Text>
                        </Descriptions.Item>
                    </Descriptions>

                    {/* Informations complémentaires du profil */}
                    <div style={{ marginTop: 24 }}>
                        <Title level={5} style={{ marginBottom: 16, color: '#595959' }}>
                            <UserOutlined style={{ marginRight: 8 }} />
                            Informations complémentaires
                        </Title>
                        <Descriptions
                            bordered
                            column={{ xs: 1, sm: 1, md: 2 }}
                            size={controlSize}
                            labelStyle={{
                                fontWeight: 500,
                                backgroundColor: '#fafafa',
                                width: isMobile ? 140 : 180
                            }}
                            contentStyle={{ backgroundColor: 'white' }}
                        >
                            <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 8, color: '#1890ff' }} />Tranche d'âge</>}>
                                {partenaire.trancheAge ? (
                                    <Tag color="blue">{partenaire.trancheAge}</Tag>
                                ) : (
                                    <Text type="secondary">Non renseigné</Text>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label={<><HeartOutlined style={{ marginRight: 8, color: '#eb2f96' }} />Situation matrimoniale</>}>
                                {partenaire.situationMatrimoniale ? (
                                    <Tag color="purple">{partenaire.situationMatrimoniale}</Tag>
                                ) : (
                                    <Text type="secondary">Non renseigné</Text>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label={<><TeamOutlined style={{ marginRight: 8, color: '#52c41a' }} />Nombre d'enfants</>}>
                                {partenaire.nbreEnfant ? (
                                    <Tag color="green">{partenaire.nbreEnfant}</Tag>
                                ) : (
                                    <Text type="secondary">Non renseigné</Text>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label={<><HomeOutlined style={{ marginRight: 8, color: '#fa8c16' }} />Résidence</>}>
                                {partenaire.residence || <Text type="secondary">Non renseigné</Text>}
                            </Descriptions.Item>
                            <Descriptions.Item label={<><SmileOutlined style={{ marginRight: 8, color: '#722ed1' }} />Fumeur</>}>
                                {partenaire.fumeur ? (
                                    <Tag color={partenaire.fumeur === 'oui' || partenaire.fumeur === 'Oui' ? 'orange' : 'green'}>
                                        {partenaire.fumeur}
                                    </Tag>
                                ) : (
                                    <Text type="secondary">Non renseigné</Text>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label={<><AlertOutlined style={{ marginRight: 8, color: '#f5222d' }} />Tél. urgence</>}>
                                {partenaire.numeroTelephoneEnCasDurgence ? (
                                    <Tag color="red" style={{ fontSize: 13 }}>{formatPhoneNumber(partenaire.numeroTelephoneEnCasDurgence)}</Tag>
                                ) : (
                                    <Text type="secondary">Non renseigné</Text>
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                </Card>
            )
        },
        {
            key: '2',
            label: (
                <span><CarOutlined style={{ marginRight: 6 }} />Véhicules</span>
            ),
            children: (
                <Card
                    style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    bodyStyle={{ padding: isMobile ? 12 : 24 }}
                >
                    <Table
                        size={controlSize}
                        columns={columnsVehicules}
                        dataSource={partenaire.vehicules}
                        rowKey="id"
                        rowClassName={rowClassName}
                        pagination={{
                            pageSize: 5,
                            showSizeChanger: !isMobile,
                            showLessItems: isMobile,
                        }}
                        scroll={{ x: 600 }}
                    />
                </Card>
            )
        },
        {
            key: '3',
            label: (
                <span><DollarOutlined style={{ marginRight: 6 }} />Revenu</span>
            ),
            children: (
                <Card
                    style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    bodyStyle={{ padding: isMobile ? 12 : 24 }}
                >
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                        <Col xs={12} md={6}>
                            <Card style={{ background: 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)', borderRadius: 12, border: 'none' }}>
                                <Statistic
                                    title={<span style={{ color: 'rgba(255,255,255,0.85)' }}><TrophyOutlined /> Points disponibles</span>}
                                    value={points}
                                    precision={2}
                                    suffix="pts"
                                    valueStyle={{ color: 'white', fontWeight: 'bold' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card style={{ background: 'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)', borderRadius: 12, border: 'none' }}>
                                <Statistic
                                    title={<span style={{ color: 'rgba(255,255,255,0.85)' }}><ClockCircleOutlined /> Heures ce mois</span>}
                                    value={partenaire?.statistiquesHeuresMensuelles?.heures ?? 0}
                                    suffix="h"
                                    valueStyle={{ color: 'white', fontWeight: 'bold' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card style={{ background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)', borderRadius: 12, border: 'none' }}>
                                <Statistic
                                    title={<span style={{ color: 'rgba(255,255,255,0.85)' }}><CarOutlined /> Courses (bonus 55)</span>}
                                    value={coursesSinceStart}
                                    valueStyle={{ color: 'white', fontWeight: 'bold' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card style={{ background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)', borderRadius: 12, border: 'none' }}>
                                <Statistic
                                    title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Bonus 130h</span>}
                                    value={partenaire?.bonusCentTrenteHeuresAttribue ? 'Oui' : 'Non'}
                                    valueStyle={{ color: 'white', fontWeight: 'bold', fontSize: 20 }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Descriptions
                        bordered
                        column={1}
                        size={controlSize}
                        labelStyle={{ fontWeight: 500, backgroundColor: '#fafafa', width: isMobile ? 180 : 250 }}
                    >
                        <Descriptions.Item label="Date dernière mise à jour points">
                            {partenaire?.soldePoints?.updatedAt ? formatDate(partenaire.soldePoints.updatedAt) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Mois dernière mise à jour">
                            {partenaire?.soldePoints?.moisDerniereMiseAJour ?? '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Bonus Lundis">
                            <Tag color={partenaire?.bonusLundis ? 'green' : 'default'}>
                                {partenaire?.bonusLundis ? 'Activé' : 'Non'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Bonus 55e course">
                            {bonus55Awarded ? (
                                <Tag color="green">Attribué le {bonus55AwardedAt?.format('DD/MM/YYYY HH:mm')}</Tag>
                            ) : bonus55NotStarted ? (
                                <Tag color="default">À venir — début le {bonus55Start?.format('DD/MM/YYYY')}</Tag>
                            ) : (
                                <>
                                    <Tag color="orange">En cours</Tag>
                                    <div style={{ marginTop: 8 }}>
                                        <Progress
                                            percent={progress55}
                                            size="small"
                                            status="active"
                                            strokeColor={{ '0%': '#fa8c16', '100%': '#52c41a' }}
                                            format={() => `${coursesSinceStart}/${GOAL_55}`}
                                        />
                                        {bonus55Start && (
                                            <div style={{ marginTop: 4, color: '#888', fontSize: 12 }}>
                                                Comptage démarré le {bonus55Start.format('DD/MM/YYYY')}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Bonus 130 heures">
                            {bonus130hAwarded ? (
                                <Tag color="green">Attribué</Tag>
                            ) : (
                                <>
                                    <Tag color="blue">En cours</Tag>
                                    <div style={{ marginTop: 8 }}>
                                        <Progress
                                            percent={progress130h}
                                            size="small"
                                            status="active"
                                            strokeColor={{ '0%': '#1890ff', '100%': '#52c41a' }}
                                            format={() => `${heuresCeMois}h/${GOAL_130H}h`}
                                        />
                                        <div style={{ marginTop: 4, color: '#888', fontSize: 12 }}>
                                            Mois en cours : {moisEnCours}
                                        </div>
                                    </div>
                                </>
                            )}
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            )
        },
        {
            key: '4',
            label: (
                <span><SettingOutlined style={{ marginRight: 6 }} />Opérations</span>
            ),
            children: (
                <Card
                    style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    bodyStyle={{ padding: isMobile ? 12 : 24 }}
                >
                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                        <Col xs={24} md={8}>
                            <RangePicker
                                size={controlSize}
                                style={{ width: '100%', borderRadius: 8 }}
                                format="DD/MM/YYYY"
                                value={opsDateRange}
                                onChange={(range) => { setOpsDateRange(range); setOpsPage(1); }}
                                placeholder={['Date début', 'Date fin']}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <Select
                                size={controlSize}
                                mode="multiple"
                                allowClear
                                style={{ width: '100%' }}
                                placeholder="Filtrer par type"
                                value={opsTypes}
                                onChange={(vals) => { setOpsTypes(vals); setOpsPage(1); }}
                                options={[
                                    { value: 'sos_credit', label: 'SOS Crédit' },
                                    { value: 'transfert', label: 'Transfert' },
                                    { value: 'vehicule_modifie', label: 'Changement véhicule' },
                                    { value: 'promo_dimanche', label: 'Promo dimanche' },
                                    { value: 'changement_numero', label: 'Changement n°' },
                                ]}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <Input
                                size={controlSize}
                                allowClear
                                prefix={<SearchOutlined style={{ color: '#fa8c16' }} />}
                                placeholder="Recherche..."
                                value={opsQuery}
                                onChange={(e) => { setOpsQuery(e.target.value); setOpsPage(1); }}
                                style={{ borderRadius: 8 }}
                            />
                        </Col>
                    </Row>

                    <Table
                        size={controlSize}
                        loading={opsLoading}
                        columns={columnsOperations}
                        dataSource={opsItems}
                        rowKey="_id"
                        rowClassName={rowClassName}
                        pagination={{
                            current: opsPage,
                            pageSize: opsLimit,
                            total: opsTotal,
                            showSizeChanger: true,
                            pageSizeOptions: [5, 10, 20, 50],
                            showLessItems: isMobile,
                            showTotal: (t, range) => <span style={{ color: '#8c8c8c' }}>{range[0]}-{range[1]} sur {t}</span>
                        }}
                        onChange={onOpsTableChange}
                        scroll={{ x: 700 }}
                    />

                    <div style={{ textAlign: 'right', marginTop: 16, padding: '12px 16px', background: '#f6ffed', borderRadius: 8 }}>
                        <Text strong style={{ fontSize: 14 }}>
                            Somme des montants (après filtres) : <Text style={{ color: '#52c41a', fontSize: 16 }}>{Number(opsSum || 0).toLocaleString('fr-FR')} FCFA</Text>
                        </Text>
                    </div>
                </Card>
            )
        },
        {
            key: '5',
            label: (
                <span><HistoryOutlined style={{ marginRight: 6 }} />Historique du compte</span>
            ),
            children: (
                <Card
                    style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    bodyStyle={{ padding: isMobile ? 12 : 24 }}
                >
                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                        <Col xs={24} md={8}>
                            <RangePicker
                                size={controlSize}
                                style={{ width: '100%', borderRadius: 8 }}
                                format="DD/MM/YYYY"
                                value={phDateRange}
                                onChange={setPhDateRange}
                                placeholder={['Date début', 'Date fin']}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <Select
                                size={controlSize}
                                allowClear
                                style={{ width: '100%' }}
                                placeholder="Filtrer par type"
                                value={phType}
                                onChange={setPhType}
                                options={[
                                    { value: 'points_rechargement', label: 'Points rechargement' },
                                    { value: 'sos_credit_montant', label: 'SOS crédit (montant)' },
                                    { value: 'sos_credit_frais', label: 'SOS crédit (frais)' },
                                    { value: 'bonus_dimanche', label: 'Bonus dimanche' },
                                    { value: 'transfert_uv', label: 'Transfert UV' },
                                    { value: 'maj_classe_service', label: 'Classe de service' },
                                    { value: 'lier_vehicule', label: 'Liaison véhicule' },
                                    { value: 'detacher_vehicule', label: 'Détacher véhicule' },
                                    { value: 'retrait_points_virement', label: 'Virement' },
                                    { value: 'bonus_130_heures', label: 'Bonus 130ième Heure' },
                                    { value: 'bonus_55_courses', label: 'Bonus 55ième Course' },
                                ]}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <Input
                                size={controlSize}
                                allowClear
                                prefix={<SearchOutlined style={{ color: '#fa8c16' }} />}
                                placeholder="Recherche..."
                                value={phQuery}
                                onChange={(e) => setPhQuery(e.target.value)}
                                style={{ borderRadius: 8 }}
                            />
                        </Col>
                    </Row>

                    <Table
                        size={controlSize}
                        columns={[
                            {
                                title: 'Type',
                                dataIndex: 'type',
                                width: 150,
                                render: (t) => <Tag color="blue">{t}</Tag>
                            },
                            {
                                title: 'Description',
                                dataIndex: 'description',
                                ellipsis: true
                            },
                            {
                                title: 'Avant',
                                dataIndex: 'soldeAvant',
                                width: 100,
                                align: 'right',
                                render: format2
                            },
                            {
                                title: 'Points',
                                dataIndex: 'points',
                                width: 100,
                                align: 'center',
                                render: (pts) => (
                                    <Tag
                                        color={pts >= 0 ? 'green' : 'red'}
                                        style={{ fontWeight: 600, fontSize: 13 }}
                                    >
                                        {pts > 0 ? `+${pts}` : pts}
                                    </Tag>
                                ),
                            },
                            {
                                title: 'Après',
                                dataIndex: 'soldeApres',
                                width: 100,
                                align: 'right',
                                render: format2
                            },
                            {
                                title: 'Date',
                                dataIndex: 'date',
                                width: 150,
                                render: (d) => (
                                    <span style={{ color: '#595959' }}>
                                        <CalendarOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
                                        {formatDate(d)}
                                    </span>
                                )
                            },
                        ]}
                        dataSource={filteredPointHistory}
                        rowKey="_id"
                        rowClassName={rowClassName}
                        pagination={{
                            current: phPage,
                            pageSize: phLimit,
                            total: filteredPointHistory.length,
                            showSizeChanger: true,
                            showLessItems: isMobile,
                            pageSizeOptions: [5, 10, 20, 50],
                            onChange: (page, pageSize) => { setPhPage(page); setPhLimit(pageSize); },
                            showTotal: (t, range) => <span style={{ color: '#8c8c8c' }}>{range[0]}-{range[1]} sur {t}</span>
                        }}
                        scroll={{ x: 800 }}
                    />

                    <div style={{ textAlign: 'right', marginTop: 16, padding: '12px 16px', background: '#f0f5ff', borderRadius: 8 }}>
                        <Text strong style={{ fontSize: 14 }}>
                            Somme des points (après filtres) : <Text style={{ color: '#1890ff', fontSize: 16 }}>{phTotalPoints.toLocaleString('fr-FR')} pts</Text>
                        </Text>
                    </div>
                </Card>
            )
        }
    ];

    return (
        <div className="content-wrapper">
            <style>
                {`
                    .table-row-light { background-color: #ffffff; }
                    .table-row-dark { background-color: #fffbe6; }
                    .ant-table-tbody > tr:hover > td { background-color: #fff7e6 !important; }
                    .ant-table-thead > tr > th {
                        background: #fafafa !important;
                        color: #262626 !important;
                        font-weight: 600 !important;
                    }
                    .ant-tabs-tab { font-weight: 500; }
                    .ant-tabs-tab-active { color: #fa8c16 !important; }
                    .ant-tabs-ink-bar { background: #fa8c16 !important; }
                `}
            </style>

            <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
                {/* Breadcrumb */}
                <Breadcrumb
                    separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
                    style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
                >
                    <Breadcrumb.Item>
                        <Link to="/dashboard" style={{ color: '#fa8c16' }}>Accueil</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Link to="/dashboard/partenaires" style={{ color: '#fa8c16' }}>Partenaires TIGOON</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <span style={{ color: '#595959' }}>Détails</span>
                    </Breadcrumb.Item>
                </Breadcrumb>

                {/* Statistiques rapides */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={12} sm={6}>
                        <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c' }}><TrophyOutlined /> Points</span>}
                                value={points}
                                precision={2}
                                valueStyle={{ color: '#52c41a', fontSize: 20 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c' }}><CarOutlined /> Véhicules</span>}
                                value={partenaire.vehicules?.length || 0}
                                valueStyle={{ color: '#1890ff', fontSize: 20 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c' }}><ClockCircleOutlined /> Heures/mois</span>}
                                value={partenaire?.statistiquesHeuresMensuelles?.heures ?? 0}
                                valueStyle={{ color: '#722ed1', fontSize: 20 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c' }}><SettingOutlined /> Opérations</span>}
                                value={opsTotal}
                                valueStyle={{ color: '#fa8c16', fontSize: 20 }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Onglets */}
                <Card
                    style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                    bodyStyle={{ padding: isMobile ? 12 : 24 }}
                >
                    <Tabs
                        defaultActiveKey="1"
                        size={controlSize}
                        items={tabItems}
                        tabBarGutter={isMobile ? 8 : 24}
                    />
                </Card>
            </div>
        </div>
    );
};

export default DetailPartenaire;
